import useFormat from "hooks/useFormat";
import { optionFromNullable } from "io-ts-types/lib/optionFromNullable";
import React from "react";
import { defineMessage, useIntl } from "react-intl";
import { useColorScaleVDP, VerticalDotPlotAuto } from "../charts-motion";
import { withFigureIO } from "../components/figure";
import { useConfig } from "../config";
import { metadata } from "../data/data_learning_fig_EGRA";
import { datasets } from "../data/data_learning_fig_EGRA_datasets";
import { countryIdIso, getCountryName, mkGemDataDecoder } from "../domain";
import { useMedia } from "../hooks";
import { fromNewtype, unsafeFromArray } from "../lib";
import * as M from "../materials";
import { breakpoints } from "../materials/consts.stylex";
import { Ar, identity, io, O, Ord, pipe } from "../prelude";
export * from "../data/data_learning_fig_EGRA";
const yUnitLabel = defineMessage({
    id: "fig.learning.EGRA.yUnitLabel",
    defaultMessage: "Percentage of students who can't read a single word"
});
const yUnitLabelShort = defineMessage({
    id: "fig.learning.EGRA.yUnitLabelShort",
    defaultMessage: "Pct. of students who can't read a single word"
});
const gradeOrder = {
    "Grade 2": 0,
    "Grade 3": 1
};
const Grade = io.keyof(gradeOrder);
const gradeLookup = {
    "Grade 2": defineMessage({
        id: "fig.learning.EGRA.grade2",
        defaultMessage: "Grade 2"
    }),
    "Grade 3": defineMessage({
        id: "fig.learning.EGRA.grade3",
        defaultMessage: "Grade 3"
    })
};
export const Data = mkGemDataDecoder([
    "year",
    "ind_id"
], {
    value: io.number,
    country: fromNewtype(io.string),
    language_or_region: optionFromNullable(io.string),
    grade: Grade
});
export const Chart = ({ data: rawData })=>{
    const intl = useIntl();
    const env = useConfig();
    const screenSDown = !useMedia(breakpoints.s);
    const screenMDown = !useMedia(breakpoints.m);
    const data = React.useMemo(()=>{
        return rawData.map((x)=>{
            const country_name = getCountryName(env.countries, x.country);
            return {
                ...x,
                country_name,
                country_language: pipe(x.language_or_region, O.fold(()=>country_name, (lr)=>`${country_name} – ${lr}`)),
                country_language_id: pipe(x.language_or_region, O.fold(()=>countryIdIso.unwrap(x.country), (lr)=>`${countryIdIso.unwrap(x.country)}:${lr}`)),
                grade_name: intl.formatMessage(gradeLookup[x.grade])
            };
        });
    }, [
        env.countries,
        intl,
        rawData
    ]);
    const chartData = React.useMemo(()=>{
        return unsafeFromArray(Ar.sortBy([
            Ord.contramap((x)=>gradeOrder[x.grade])(Ord.ordNumber),
            Ord.contramap((x)=>x.value)(Ord.ordNumber)
        ])(data));
    }, [
        data
    ]);
    const getX = React.useCallback((x)=>x.country_language, []);
    const getValue = React.useCallback((x)=>x.value, []);
    const getColor = React.useCallback((x)=>x.grade_name, []);
    const getColorPalette = React.useCallback(()=>M.colorRanges.discrete, []);
    const formatY = useFormat(".0%");
    const [colorScale, colorLegendValues] = useColorScaleVDP({
        data: chartData,
        getColor,
        colorPalette: getColorPalette,
        formatColor: identity
    });
    return <VerticalDotPlotAuto height={screenMDown ? M.chartHeight.s : M.chartHeight.m} data={chartData} getX={getX} getValue={getValue} getColor={getColor} formatX={identity} formatValue={formatY} domainY={[
        0,
        1
    ]} colorScale={colorScale} colorLegendValues={colorLegendValues} showTooltip showLegend showYExtent dotStyle={screenSDown ? "small" : "medium"} labelStyleX="none" yUnitLabel={screenSDown ? intl.formatMessage(yUnitLabelShort) : intl.formatMessage(yUnitLabel)}/>;
};
export default withFigureIO({
    url: new URL("../data/data_learning_fig_EGRA.json", import.meta.url).href,
    csv: new URL("../data/data_learning_fig_EGRA.zip", import.meta.url).href,
    xlsx: new URL("../data/data_learning_fig_EGRA.xlsx", import.meta.url).href,
    metadata,
    Data,
    Chart,
    datasets
});
